import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { RiDashboardLine } from "react-icons/ri";
import { IoClose } from "react-icons/io5";
import { SiHackthebox } from "react-icons/si";
import { TbCategory2 } from "react-icons/tb";
import { MdOutlineCategory, MdKeyboardReturn } from "react-icons/md";
import { FiHome, FiLayers, FiShoppingCart, FiBarChart2, FiBox } from "react-icons/fi";

const SupplierSidebar = ({ isSidebarOpen, setIsSidebarOpen }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const seller = JSON.parse(localStorage.getItem("supplierData"));
  const permissions = seller?.permissions || {};

  const menuItems = [
    {
      label: "Dashboard",
      icon: <RiDashboardLine size={20} />,
      path: "/seller/dashboard",
      permission: "supplier-dashboard",
    },
    {
      label: "Categories",
      icon: <TbCategory2 size={20} />,
      path: "/seller/categories",
      permission: "supplier-categories",
    },
    {
      label: "Sub Categories",
      icon: <MdOutlineCategory size={20} />,
      path: "/seller/sub-categories",
      permission: "supplier-subCategories",
    },
    {
      label: "Products",
      icon: <SiHackthebox size={20} />,
      path: "/seller/products",
      permission: "supplier-products",
    },
    {
      label: "Variants",
      icon: <FiLayers size={20} />,
      path: "/seller/variants",
    },
    {
      label: "Inventory",
      icon: <FiBox size={20} />,
      path: "/seller/inventory",
    },
    {
      label: "Orders",
      icon: <FiShoppingCart size={20} />,
      path: "/seller/orders",
    },
    {
      label: "Returns",
      icon: <MdKeyboardReturn size={20} />,
      path: "/seller/returns",
    },
    {
      label: "Report Analysis",
      icon: <FiBarChart2 size={20} />,
      path: "/seller/reports",
    },
  ].filter(item => !item.permission || permissions[item.permission] === true);

  const renderMenuItems = (isMobile = false) => (
    <ul className="space-y-1 mt-3">
      {menuItems.map((item, index) => (
        <motion.li
          key={item.path}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: index * 0.1 }}
          whileHover={{ 
            scale: 1.02,
            transition: { duration: 0.2 }
          }}
          whileTap={{ scale: 0.98 }}
        >
          <button
            onClick={() => {
              navigate(item.path);
              if (window.innerWidth < 1024) {
                setIsSidebarOpen(false);
              }
            }}
            className={`flex items-center w-full p-3 text-sm tracking-wide rounded-xl transition-all duration-300 group ${
              pathname === item.path
                ? "bg-[#fba93d] text-white shadow-lg shadow-orange-500/25"
                : "text-gray-600 hover:bg-gray-50/10 hover:text-white dark:text-gray-300 dark:hover:bg-gray-700/50"
            } ${isMobile ? "bg-white/5" : ""}`}
          >
            <span
              className={`transition-all duration-300 ${
                pathname === item.path
                  ? "text-white scale-110"
                  : "text-[#fba93d] group-hover:text-white group-hover:scale-110"
              }`}
            >
              {item.icon}
            </span>
            <span className={`ml-3 font-normal tracking-wider transition-all duration-300 ${
              pathname === item.path ? "text-white" : "text-gray-300 group-hover:text-white"
            }`}>
              {item.label}
            </span>
            {pathname === item.path && (
              <motion.span
                className="ml-auto h-2 w-2 bg-white rounded-full"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 500, damping: 15 }}
              />
            )}
          </button>
        </motion.li>
      ))}
    </ul>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <div className="hidden lg:flex lg:flex-shrink-0">
        <motion.div 
          className="flex flex-col w-72 h-screen overflow-y-auto bg-[#131921] relative"
          initial={{ x: -100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          {/* Decorative Elements */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-[#fba93d]/10 rounded-full -translate-y-16 translate-x-16 blur-2xl"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-[#014470]/30 rounded-full translate-y-12 -translate-x-12 blur-xl"></div>
          
          {/* Header */}
          <div className="relative py-3.5 px-6 bg-[#131921] border-b border-white/10 shadow-lg">
            <div className="flex items-center gap-3">
              <motion.div
                whileHover={{ scale: 1.1, rotate: 5 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => navigate("/")}
                className="p-1.5 bg-white/10 rounded-xl hover:bg-white/20 cursor-pointer transition-all duration-300 backdrop-blur-sm border border-white/10"
              >
                <FiHome size={16} className="text-white" />
              </motion.div>
              <div>
                <motion.h2 
                  className="text-xl font-philosopher text-white tracking-wide"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                >
                  SELLER MENU
                </motion.h2>
              </div>
            </div>
          </div>

          {/* Navigation Items */}
          <div className="relative px-4 py-4 flex-1">
            <div className="relative">
              {renderMenuItems()}
            </div>
          </div>
        </motion.div>
      </div>

      {/* Mobile Sidebar */}
      <div className="block lg:hidden">
        {/* Overlay */}
        <AnimatePresence>
          {isSidebarOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
              onClick={toggleSidebar}
            />
          )}
        </AnimatePresence>

        {/* Sidebar Content */}
        <AnimatePresence>
          {isSidebarOpen && (
            <motion.div
              className="fixed inset-y-0 left-0 z-50 w-80 h-full"
              initial={{ x: "-100%", opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: "-100%", opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
            >
              <div className="flex flex-col w-full h-full overflow-y-auto bg-gradient-to-br from-[#00395F] via-[#014470] to-[#001F36] relative">
                {/* Decorative Elements for Mobile */}
                <div className="absolute top-0 right-0 w-40 h-40 bg-[#fba93d]/10 rounded-full -translate-y-20 translate-x-20 blur-2xl"></div>
                
                {/* Header */}
                <div className="relative py-6 px-6 bg-gradient-to-r from-[#073a5c] to-[#08527a] border-b border-white/10">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div
                        onClick={() => navigate("/")}
                        className="p-2 bg-white/10 rounded-xl hover:bg-white/20 cursor-pointer transition-all duration-300"
                      >
                        <FiHome size={20} className="text-white" />
                      </div>
                      <div>
                        <h2 className="text-xl font-bold font-philosopher text-white tracking-wide">
                          SELLER MENU
                        </h2>
                        <p className="text-xs text-white/60 mt-1">Navigation Panel</p>
                      </div>
                    </div>
                    <motion.button
                      whileHover={{ scale: 1.1, rotate: 90 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={toggleSidebar}
                      className="p-2 rounded-xl bg-white/10 hover:bg-white/20 transition-all duration-300 backdrop-blur-sm border border-white/10"
                    >
                      <IoClose size={20} className="text-white" />
                    </motion.button>
                  </div>
                </div>

                {/* Navigation Items */}
                <div className="relative px-4 py-6 flex-1">
                  {renderMenuItems(true)}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
};

export default SupplierSidebar;